<template>
  <li
    :ref="`sb-combobox-selected-${inputId}`"
    v-tooltip="showTooltip ? label + (details ? ' - ' + details : '') : ''"
    :aria-details="`item-${dataTestid}-details`"
    :aria-labelledby="`item-${dataTestid}-label`"
    :aria-selected="selected"
    :aria-current="navigationalSelect && selected ? 'page' : undefined"
    :aria-disabled="isDisabled"
    :class="computedClasses"
    class="sb-select-list__item"
    role="option"
    tabindex="-1"
    @click="handleClick"
    @keydown="handleKeyDown"
  >
    <slot name="list-item" :item="option">
      <SbAvatar
        v-if="useAvatars"
        :key="label"
        :src="option.src"
        :name="label"
        :data-testid="`${dataTestid}-avatar`"
        size="small"
        :show-name="false"
      />

      <SbIcon
        v-if="option.icon"
        class="sb-select-list__item-icon"
        :data-testid="`${dataTestid}-icon`"
        :name="option.icon"
      />

      <div class="sb-select-list__item-label-wrapper">
        <span
          :id="`item-${dataTestid}-label`"
          ref="labelEl"
          class="sb-select-list__item-label"
          :data-testid="`${dataTestid}-label`"
        >
          {{ label }}
        </span>
        <span
          v-if="showCaption && details"
          :id="`item-${dataTestid}-details`"
          ref="detailsEl"
          class="sb-select-list__item-caption"
          :data-testid="`${dataTestid}-details`"
        >
          {{ details }}
        </span>
      </div>
    </slot>
  </li>
</template>

<script lang="ts">
import { ref, onMounted, onUpdated, onBeforeUnmount, nextTick } from 'vue'
import type { PropType } from 'vue'
import SbAvatar from '../../Avatar'
import SbIcon from '../../Icon'
import type { SelectValue } from '../types'
import { Tooltip } from '../../../directives'

export default {
  name: 'SbSelectListItem',

  directives: {
    tooltip: Tooltip,
  },

  components: {
    SbAvatar,
    SbIcon,
  },

  props: {
    inputId: {
      type: String,
      default: '',
    },
    inputValue: {
      type: [String, Number] as PropType<SelectValue>,
      default: null,
    },
    label: {
      type: String,
      default: '',
    },
    modelValue: {
      type: [String, Number],
      default: '',
    },
    option: {
      type: Object,
      default: () => ({}),
    },
    itemValue: {
      type: String,
      default: 'value',
    },
    emitOption: Boolean,
    useAvatars: Boolean,
    src: {
      type: String,
      default: null,
    },
    selected: Boolean,
    showCaption: Boolean,
    details: {
      type: String,
      default: null,
    },
    isDisabled: Boolean,
    navigationalSelect: Boolean,
  },

  emits: ['emit-value'],

  computed: {
    dataTestid() {
      return this.$attrs['data-testid'] || 'sb-select-list-item'
    },

    computedClasses() {
      return [
        this.selected && 'sb-select-list__item--selected',
        this.isDisabled && 'sb-select-list__item--disabled',
      ]
    },
  },

  setup() {
    const labelEl = ref<HTMLElement | null>(null)
    const detailsEl = ref<HTMLElement | null>(null)
    const showTooltip = ref(false)

    const checkTruncation = () => {
      nextTick(() => {
        let labelTruncated = false
        let detailsTruncated = false

        if (labelEl.value) {
          labelTruncated =
            labelEl.value.scrollWidth > labelEl.value.clientWidth ||
            labelEl.value.scrollHeight > labelEl.value.clientHeight
        }
        if (detailsEl.value) {
          detailsTruncated =
            detailsEl.value.scrollWidth > detailsEl.value.clientWidth
        }
        showTooltip.value = labelTruncated || detailsTruncated
      })
    }

    const handleResize = () => checkTruncation()

    onMounted(() => {
      checkTruncation()
      window.addEventListener('resize', handleResize)
    })

    onUpdated(() => {
      checkTruncation()
    })

    onBeforeUnmount(() => {
      window.removeEventListener('resize', handleResize)
    })

    return {
      labelEl,
      detailsEl,
      showTooltip,
    }
  },

  methods: {
    handleEmitValue() {
      if (this.emitOption) {
        return this.$emit('emit-value', this.option)
      }
      this.$emit('emit-value', this.modelValue)
    },

    handleClick() {
      if (this.isDisabled) return
      this.handleEmitValue()
    },

    handleKeyDown(event: KeyboardEvent) {
      if (this.isDisabled) return
      if (event.key === 'Enter' || event.key === ' ') {
        this.handleEmitValue()
      }
    },
  },
}
</script>
